/**
 * Shared system-message payload validation and plain-text formatting.
 * Presentation-only: no Redux, auth, or ownership logic.
 */

export const SYSTEM_MESSAGE_FALLBACK = "Системна подія";

const ACTIONS_REQUIRING_TARGET = new Set([
  "promote",
  "demote",
  "add",
  "remove",
  "transfer_owner",
]);

const ACTIONS_ACTOR_ONLY = new Set([
  "leave",
  "rename_chat",
  "update_chat_image",
]);

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function normalizeParticipant(participant) {
  if (!isPlainObject(participant)) return null;
  const id = participant.id;
  const fullname = participant.fullname;
  if (id == null || id === "") return null;
  if (typeof fullname !== "string" || !fullname) return null;
  const handle =
    typeof participant.handle === "string" && participant.handle
      ? participant.handle
      : null;
  const publicId =
    participant.public_id != null && participant.public_id !== ""
      ? String(participant.public_id)
      : null;
  return { id, fullname, handle, public_id: publicId };
}

/**
 * Returns a validated system payload, or null when malformed/unknown.
 */
export function normalizeSystemData(systemData) {
  if (!isPlainObject(systemData)) return null;
  const action = systemData.action;
  if (typeof action !== "string" || !action) return null;
  if (!ACTIONS_REQUIRING_TARGET.has(action) && !ACTIONS_ACTOR_ONLY.has(action)) {
    return null;
  }

  const actor = normalizeParticipant(systemData.actor);
  if (!actor) return null;

  let target = null;
  if (ACTIONS_REQUIRING_TARGET.has(action)) {
    target = normalizeParticipant(systemData.target);
    if (!target) return null;
  } else if (systemData.target != null) {
    // Optional participant; ignore malformed optional target without failing.
    target = normalizeParticipant(systemData.target);
  }

  const normalized = { action, actor, target };
  if (action === "rename_chat" && typeof systemData.new_name === "string") {
    normalized.new_name = systemData.new_name;
  }
  return normalized;
}

/**
 * Plain-text preview for chat list last-message rows.
 */
export function formatSystemMessagePreview(systemData) {
  const data = normalizeSystemData(systemData);
  if (!data) return SYSTEM_MESSAGE_FALLBACK;

  const { action, actor, target, new_name: newName } = data;
  switch (action) {
    case "promote":
      return `${actor.fullname} підвищив ${target.fullname} до адміністратора`;
    case "demote":
      return `${actor.fullname} понизив ${target.fullname} до учасника`;
    case "add":
      return `${actor.fullname} додав ${target.fullname}`;
    case "remove":
      return `${actor.fullname} видалив ${target.fullname}`;
    case "leave":
      return `${actor.fullname} вийшов з чату`;
    case "transfer_owner":
      return `${actor.fullname} передав права власника ${target.fullname}`;
    case "rename_chat":
      return `${actor.fullname} змінив назву чату${newName ? ` на "${newName}"` : ""}`;
    case "update_chat_image":
      return `${actor.fullname} оновив фото чату`;
    default:
      return SYSTEM_MESSAGE_FALLBACK;
  }
}

/**
 * Select private-chat counterpart members, excluding the current user.
 * Prefers email match; falls back to user id when email is unavailable.
 */
export function selectPrivatePeerMembers(members, myEmail, myUserId) {
  const list = Array.isArray(members) ? members : [];
  return list.filter((member) => {
    const user = member?.user;
    if (!user) return false;
    if (myEmail != null && myEmail !== "") {
      return user.email !== myEmail;
    }
    if (myUserId != null && myUserId !== "") {
      return String(user.id) !== String(myUserId);
    }
    return true;
  });
}
